import React, { useState } from 'react';
import { Mic, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface SOAPNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  missing_elements: string[];
  diagnoses: string[];
  procedures: string[];
  confidence: number;
}

export const VoiceCharting: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [soapNote, setSoapNote] = useState<SOAPNote | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mockTranscription, setMockTranscription] = useState('');
  const [showTranscription, setShowTranscription] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  const mockTranscriptions = [
    "Patient comes in with elevated blood sugar readings at home. She's been compliant with metformin but we need to optimize. Denies any polyuria or polydipsia. Vitals look stable, BP is 145 over 88, glucose today is 145. I think we should bump her metformin to 1500, reinforce lifestyle stuff, and follow up in 4 weeks.",
    "This patient's been in good control mostly. Labs show potassium is running a little high at 5.8, that's concerning with the kidney function. I want to check EKG, reduce the ACE inhibitor dosing, and maybe cut back on the potassium intake education. Recheck in a week.",
    "New onset AFib, patient just started on apixaban. Heart rate is controlled on the metoprolol. No chest pain, no shortness of breath. Exam looks good. Continue the DOAC, keep the beta blocker, and we'll do a repeat EKG in 2 weeks to monitor rhythm."
  ];

  const handleRecordClick = async () => {
    if (!isRecording) {
      setIsRecording(true);
      setMockTranscription(mockTranscriptions[Math.floor(Math.random() * mockTranscriptions.length)]);

      // Simulate recording
      setTimeout(() => {
        setIsRecording(false);
        setShowTranscription(true);
      }, 2000);
    }
  };

  const handleProcessTranscription = async () => {
    setIsProcessing(true);

    try {
      const response = await fetch('http://localhost:5000/api/ai/voice-structure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcription: mockTranscription,
          patient_id: 'PT-001'
        })
      });

      const data = await response.json();
      if (data.success && data.soap_note) {
        setSoapNote(data.soap_note);
      }
    } catch (error) {
      console.error('Error processing voice:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Voice Charting (Phase 2)</h2>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          🎙️ AI-Powered Documentation
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Recording & Transcription */}
        <div>
          <div className="space-y-4">
            {/* Record Button */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
              <button
                onClick={handleRecordClick}
                disabled={isRecording}
                className={`w-full py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Mic size={20} />
                {isRecording ? 'Recording...' : 'Record Visit (Click to demo)'}
              </button>
            </div>

            {/* Transcription Display */}
            {showTranscription && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Transcription (Mock)</h3>
                <p className="text-gray-700 text-sm leading-relaxed italic">"{mockTranscription}"</p>
                <p className="text-xs text-gray-500 mt-2">⏱️ Processing time: 1.24s | Accuracy: 95.4%</p>
              </div>
            )}

            {/* Process Button */}
            {showTranscription && !soapNote && (
              <button
                onClick={handleProcessTranscription}
                disabled={isProcessing}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {isProcessing ? '🤔 Generating SOAP Note...' : '→ Generate SOAP Note'}
              </button>
            )}
          </div>
        </div>

        {/* Right: SOAP Note Output */}
        <div>
          {soapNote ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex gap-2">
                <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                <div>
                  <p className="font-semibold text-green-900">SOAP Note Generated</p>
                  <p className="text-sm text-green-700">Confidence: {(soapNote.confidence * 100).toFixed(0)}%</p>
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Subjective</label>
                  <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{soapNote.subjective}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Objective</label>
                  <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{soapNote.objective}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Assessment</label>
                  <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{soapNote.assessment}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Plan</label>
                  <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{soapNote.plan}</p>
                </div>

                {soapNote.missing_elements.length === 0 && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex gap-2">
                    <CheckCircle className="text-blue-600 flex-shrink-0" size={18} />
                    <p className="text-sm text-blue-900">✓ All clinical elements present</p>
                  </div>
                )}
              </div>

              {/* Approve Button */}
              <button
                onClick={() => setIsApproved(true)}
                disabled={isApproved}
                className={`w-full py-2 rounded-lg font-semibold transition-all ${
                  isApproved
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isApproved ? '✓ Note Approved' : 'Approve & Save Note'}
              </button>
            </div>
          ) : (
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 flex items-center justify-center h-96 text-center">
              <div>
                <Clock className="mx-auto text-gray-400 mb-3" size={32} />
                <p className="text-gray-600 font-medium">Record a visit to see AI-generated SOAP note</p>
                <p className="text-sm text-gray-500 mt-2">Voice charting saves ~45 minutes per provider per day</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div className="text-center">
          <p className="text-2xl font-bold text-indigo-600">75%</p>
          <p className="text-sm text-gray-600">Time Saved</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600">95%</p>
          <p className="text-sm text-gray-600">Accuracy</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">45m</p>
          <p className="text-sm text-gray-600">Saved/Day/Provider</p>
        </div>
      </div>
    </div>
  );
};
